"use client";

import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
    useReducedMotion,
} from "framer-motion";
import { type PointerEvent, type ReactNode, useRef } from "react";
import { cn } from "@/shared/lib/utils";

type TiltCardProps = {
    children: ReactNode;
    className?: string;
    intensity?: number;
};

// A card that leans in 3D toward the cursor and springs back flat on leave.
export const TiltCard = ({
    children,
    className,
    intensity = 8,
}: TiltCardProps) => {
    const reduce = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);

    // Raw pointer offset within the card, normalised to a -0.5..0.5 range.
    const px = useMotionValue(0);
    const py = useMotionValue(0);

    // Map the offset to rotation and smooth it with a spring for a heavy feel.
    const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [intensity, -intensity]), {
        stiffness: 200,
        damping: 20,
    });
    const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-intensity, intensity]), {
        stiffness: 200,
        damping: 20,
    });

    // Translate the pointer position into the normalised offset values.
    const handleMove = (e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType !== "mouse") return;
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        px.set((e.clientX - rect.left) / rect.width - 0.5);
        py.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    // Snap the card back to flat once the cursor leaves.
    const reset = () => {
        px.set(0);
        py.set(0);
    };

    // Touch devices and reduced-motion users get a plain, static card.
    if (reduce) return <div className={className}>{children}</div>;

    return (
        <motion.div
            ref={ref}
            onPointerMove={handleMove}
            onPointerLeave={reset}
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            className={cn("transform-gpu", className)}
        >
            {children}
        </motion.div>
    );
};
